import { useRef, useState } from 'react';

import axios from 'axios';
import { DateTime } from 'luxon';

import {
  getPatientSupportFreePeriods,
  AVAILABILITY_BASE_URL,
} from '../util/calendar.util';
import { useDatadog } from '~/lib/hooks/useDatadog';
import type { ScheduledApppointmentsResponse } from '~/components/common/calendar/EditAppointment/util';

export const AVAILABILITY_RULES_BASE_URL =
  'https://api.cronofy.com/v1/availability_rules';

const AVAILABILITY_RULES_CRONOFY_ELEMENT_HEADER = {
  'cronofy-element': 'v1.54.0, AvailabilityRules',
};
const DATE_TIME_PICKER_CRONOFY_ELEMENT_HEADER = {
  'cronofy-element': 'v1.54.0, DateTimePicker',
};

const AVAILABILITY_RULES_POST_HEADERS = {
  ...AVAILABILITY_RULES_CRONOFY_ELEMENT_HEADER,
  'content-type': 'application/json',
};

const DATE_TIME_PICKER_POST_HEADERS = {
  ...DATE_TIME_PICKER_CRONOFY_ELEMENT_HEADER,
  'content-type': 'application/json',
};

type UseCalendarApiState = {
  appointmentRecurrenceCheck: (
    availabilityQuery: CalendarResourceConfigAvailabilityQuery,
    elementToken: string,
    patientAppointments: ScheduledApppointmentsResponse[],
    queryPeriodsGroup: AppointmentDetailTime[][],
    supportAppointments: ScheduledApppointmentsResponse[],
    includeAvailableSlots?: AvailabilitySlot[],
  ) => Promise<string[]>;
  getAvailabilityRule: (
    rule: string,
    token: string,
  ) => Promise<AvailabilityRule>;
  isGetAvailabilityRuleProcessing: boolean;
  isSaveAvailabilityRuleError: boolean;
  isSaveAvailabilityRuleProcessing: boolean;
  resetSaveAvailabilityRuleError: () => void;
  saveAvailabilityRule: (
    token: string,
    params?: AvailabilityRule,
  ) => Promise<number>;
  updateAvailabilityRuleRequestParams: (params: AvailabilityRule) => void;
};

const useCalendarApi = (): UseCalendarApiState => {
  const { logError } = useDatadog();
  // Store as a ref rather than in local state so that the latest object is available when saving availability
  const availabilityRuleRequestParamsRef = useRef<AvailabilityRule>(null);
  const updateAvailabilityRuleRequestParams = (
    params: AvailabilityRule,
  ): void => {
    availabilityRuleRequestParamsRef.current = {
      ...availabilityRuleRequestParamsRef?.current,
      ...params,
    };
  };

  const [isGetAvailabilityRuleProcessing, setIsGetAvailabilityRuleProcessing] =
    useState<boolean>(false);
  const [
    isSaveAvailabilityRuleProcessing,
    setIsSaveAvailabilityRuleProcessing,
  ] = useState<boolean>(false);
  const [isSaveAvailabilityRuleError, setIsSaveAvailabilityRuleError] =
    useState<boolean>(false);
  const resetSaveAvailabilityRuleError = (): void => {
    setIsSaveAvailabilityRuleError(false);
  };

  const getAvailabilityRule = async (
    availability_rule_id: string,
    elementToken: string,
  ): Promise<AvailabilityRule> => {
    setIsGetAvailabilityRuleProcessing(true);
    let result: AvailabilityRule = null;
    try {
      const response = await axios.get<{ availability_rule: AvailabilityRule }>(
        `${AVAILABILITY_RULES_BASE_URL}/${availability_rule_id}?et=${elementToken}`,
        { headers: AVAILABILITY_RULES_CRONOFY_ELEMENT_HEADER },
      );
      if (response?.status === 200) {
        result = response.data?.availability_rule;
      }
    } catch (e) {
      logError(e, {
        extra: {
          message: 'Failed to get availability rule',
        },
        tags: { logFrom: 'useCalendarApi.getAvailabilityRule' },
      });
      console.error('Failed to get availability rule', e.message);
    } finally {
      setIsGetAvailabilityRuleProcessing(false);
    }
    return result;
  };

  const saveAvailabilityRule = async (
    elementToken: string,
    params?: AvailabilityRule,
  ): Promise<number> => {
    setIsSaveAvailabilityRuleProcessing(true);
    setIsSaveAvailabilityRuleError(false);
    let status: number;
    try {
      const response = await axios.post(
        `${AVAILABILITY_RULES_BASE_URL}?et=${elementToken}`,
        params ?? availabilityRuleRequestParamsRef?.current,
        { headers: AVAILABILITY_RULES_POST_HEADERS },
      );
      status = response?.status;
    } catch (e) {
      logError(e, {
        extra: {
          message: 'Failed to save availability rule',
        },
        tags: { logFrom: 'useCalendarApi.saveAvailabilityRule' },
      });
      setIsSaveAvailabilityRuleError(true);
      console.error(e.message);
      status = 400;
    } finally {
      setIsSaveAvailabilityRuleProcessing(false);
    }
    return status;
  };

  const appointmentRecurrenceCheck = async (
    availabilityQuery: CalendarResourceConfigAvailabilityQuery,
    elementToken: string,
    patientAppointments: AppointmentDetailResponse[],
    queryPeriodsGroup: AppointmentDetailTime[][],
    supportAppointments: AppointmentDetailResponse[],
    includeAvailableSlots?: AvailabilitySlot[],
  ): Promise<string[]> => {
    let availableSlots: AppointmentDetailTime[] = [];
    const getAvailablePeriods = async (
      queryPeriods: AppointmentDetailTime[],
    ): Promise<AppointmentDetailTime[]> => {
      try {
        const queryPeriodsToUse: AppointmentDetailTime[] = [];
        queryPeriods.forEach((period: AppointmentDetailTime) =>
          queryPeriodsToUse.push(
            ...getPatientSupportFreePeriods({
              end: DateTime.fromISO(period.end),
              patientAppointments,
              start: DateTime.fromISO(period.start),
              supportAppointments,
            }),
          ),
        );
        if (queryPeriodsToUse.length) {
          const response = await axios.post(
            `${AVAILABILITY_BASE_URL}?et=${elementToken}`,
            { ...availabilityQuery, query_periods: queryPeriodsToUse },
            { headers: DATE_TIME_PICKER_POST_HEADERS },
          );
          availableSlots = response?.data.available_slots;
        }
      } catch (e) {
        logError(e, {
          extra: {
            message: 'Failed to get available periods',
          },
          tags: { logFrom: 'useCalendarApi.appointmentRecurrenceCheck' },
        });
        console.error('Failed to get available periods', e.message);
        availableSlots = [];
      }
      return availableSlots;
    };
    try {
      const conflictsRes = await Promise.all(
        queryPeriodsGroup.map(getAvailablePeriods),
      );

      const combinedConflictsRes = [
        ...conflictsRes,
        ...(includeAvailableSlots ?? []),
      ].flat();
      return queryPeriodsGroup
        .flat()
        .filter(
          (period) =>
            !combinedConflictsRes.some(
              (conflict) => conflict.end === period.end,
            ),
        )
        .map((item) => DateTime.fromISO(item.start).toFormat('MM/dd'));
    } catch (e) {
      logError(e, {
        extra: {
          message: 'Error while checking appointment recurrence availability',
        },
        tags: { logFrom: 'useCalendarApi.appointmentRecurrenceCheck' },
      });
      console.error('Failed to check appointment recurrence', e.message);
      throw new Error(e);
    }
  };

  return {
    appointmentRecurrenceCheck,
    getAvailabilityRule,
    isGetAvailabilityRuleProcessing,
    isSaveAvailabilityRuleError,
    isSaveAvailabilityRuleProcessing,
    resetSaveAvailabilityRuleError,
    saveAvailabilityRule,
    updateAvailabilityRuleRequestParams,
  };
};

export default useCalendarApi;
